import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProperties } from "../../redux/propertiesSlice";
import { RootState, AppDispatch } from "../../redux/store";
import { Property } from "../../types/property";
import { isLetAgreedStatus } from "../../util/propertyParsing";

function getMainImage(property: Property): string | undefined {
  if (property.photos?.length) {
    return property.photos[0].media_url;
  }
  if (!property.media || !Array.isArray(property.media)) return undefined;
  const main =
    property.media.find((m) => m.caption?.includes("Main")) ||
    property.media.find((m) => m.media_type === 1 && m.sort_order === 1) ||
    property.media[0];
  return main?.media_url;
}

const PropertiesPewview: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { items: properties, status } = useSelector(
    (state: RootState) => state.properties
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchProperties());
    }
  }, [dispatch, status]);

  const featuredProperties = useMemo(() => {
    return properties
      .filter((property) => !isLetAgreedStatus(property.status))
      .sort((a, b) => {
        const aDate = a.update_date ? new Date(a.update_date).getTime() : 0;
        const bDate = b.update_date ? new Date(b.update_date).getTime() : 0;
        return bDate - aDate;
      })
      .slice(0, 6);
  }, [properties]);

  const handlePropertyClick = (id: number) => {
    navigate(`/services/rent/properties/${id}`);
  };

  return (
    <section className="py-32 relative bg-bg-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5">
        <div className="mb-20 pl-4 border-l-4 border-l-brand-yellow">
          <span className="text-xs tracking-widest uppercase text-text-slate block mb-2">
            Selection
          </span>
          <h2 className="page-section-heading">Current Inventory</h2>
        </div>
        <div className="flex gap-6 pb-12 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:h-0.5 [&::-webkit-scrollbar-thumb]:bg-brand-yellow items-stretch">
          {status !== "succeeded" && (
            <div className="text-text-slate">Loading properties...</div>
          )}

          {featuredProperties.map((property) => {
            const imageUrl = getMainImage(property);
            return (
              <div
                key={property.id}
                className="min-w-[300px] cursor-pointer relative group"
                onClick={() => handlePropertyClick(property.id)}
              >
                <div className="h-[320px] w-full relative overflow-hidden clip-path-[polygon(0_0,100%_0,100%_90%,0%_100%)] transition-all duration-400 ease-in-out group-hover:clip-path-[polygon(0_0,100%_0,100%_100%,0%_100%)]">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={`${property.property_type} in ${property.display_address}`}
                      className="w-full h-full object-cover transition-all duration-800  group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-text-slate">No Image</span>
                    </div>
                  )}
                  <div className="absolute top-3 left-3 bg-white/90 text-text-obsidian px-2 py-1 text-xs uppercase tracking-wider font-manrope">
                    {property.status || "Available"}
                  </div>
                </div>
                <div className="p-4 pb-0 flex justify-between items-end">
                  <div>
                    <h3 className="text-lg mb-1 font-teko leading-tight">
                      {property.display_address}
                    </h3>
                    <span className="text-xs uppercase tracking-wider text-text-slate">
                      {property.bedrooms ?? "-"} Bed • {property.property_type}
                      {property.internal_area
                        ? ` • ${property.internal_area.toLocaleString()} sq ft`
                        : ""}
                    </span>
                  </div>
                  <div className="bg-text-obsidian text-white p-1.5 px-3 transform skew-x-[var(--skew)] group-hover:bg-brand-yellow group-hover:text-black">
                    <span className="block transform skew-x-[10deg] font-teko text-base">
                      £{property.price}{" "}
                      {property.rent_frequency === "Monthly" ? "PCM" : "PW"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="min-w-[180px] flex items-center justify-center">
            <button
              className="bg-brand-yellow hover:bg-yellow-400 text-black font-teko text-lg uppercase px-6 py-4 shadow-lg transition-all duration-300 transform skew-x-[var(--skew)] border-2 border-brand-yellow hover:border-yellow-500 min-w-[140px]"
              onClick={() => navigate("/services/rent/properties")}
              aria-label="Show more properties"
            >
              <span className="block transform skew-x-[10deg]">
                Show More &rarr;
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PropertiesPewview;
